import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TimelineCard } from '@/components/TimelineCard'
import FeedbackHub from '@/components/collaboration-detail/FeedbackHub'
import CollaborationInfoCard from '@/components/collaboration-detail/CollaborationInfoCard'
import CollaborationDocumentsCard from '@/components/collaboration-detail/CollaborationDocumentsCard'
import ComplianceTab from '@/components/collaboration-detail/ComplianceTab'
import CollaborationEventsTab from '@/components/collaboration-detail/CollaborationEventsTab'
import { useState } from 'react'
import { FeedbackMessage } from '@/hooks/useFeedbackMessages'

interface TimelineMessage {
  id: number
  body: string
  messageType: string
  createdAt: string
  metadata?: Record<string, unknown>
  milestone?: {
    id: number
    name: string
  } | null
  author?: {
    id: string
    name: string
    avatar: string | null
  } | null
}

interface CollaborationDetailTabsProps {
  campaign: any
  feedbackMessages: FeedbackMessage[]
  timeline: TimelineMessage[]
  activeTab: string
  onTabChange: (value: string) => void
  highlightEventId?: string
}

const CollaborationDetailTabs = ({
  campaign,
  feedbackMessages,
  timeline,
  activeTab,
  onTabChange,
  highlightEventId,
}: CollaborationDetailTabsProps) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true)

  const rawPayout = campaign.payout != null ? parseFloat(campaign.payout) : null
  const showComplianceBadge =
    campaign.nilGoStatus !== 'confirmed' && (rawPayout === null || rawPayout >= 600)

  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
      <TabsList className="w-full mb-4 bg-white/10 border border-white/10 overflow-x-auto overflow-y-hidden justify-start">
        <TabsTrigger
          value="feedback"
          className="flex-none text-sm data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
        >
          Feedback
        </TabsTrigger>
        {campaign.complianceNeeded && (
          <TabsTrigger
            value="compliance"
            className="flex-none text-sm relative data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
          >
            Compliance
            {showComplianceBadge && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#F9DA4C]" />
            )}
          </TabsTrigger>
        )}
        <TabsTrigger
          value="details"
          className="flex-none text-sm data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
        >
          Details
        </TabsTrigger>
        <TabsTrigger
          value="documents"
          className="flex-none text-sm data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
        >
          Docs
        </TabsTrigger>
        <TabsTrigger
          value="events"
          className="flex-none text-sm data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
        >
          Events
        </TabsTrigger>
      </TabsList>

      <TabsContent value="feedback" className="mt-6">
        <FeedbackHub campaignId={campaign.id} feedbackMessages={feedbackMessages} />
      </TabsContent>

      {campaign.complianceNeeded && (
        <TabsContent value="compliance" className="mt-6">
          <ComplianceTab campaign={campaign} />
        </TabsContent>
      )}

      <TabsContent value="details" className="mt-6 space-y-6">
        {/* Campaign Title & Image */}
        <div>
          <h1 className="text-3xl font-bold text-white mb-4">{campaign.title}</h1>
          {campaign.imageUrl && (
            <div className="h-32 w-32 rounded-xl overflow-hidden bg-white/10">
              <img
                src={campaign.imageUrl}
                alt={campaign.title}
                className="h-full w-full object-cover"
              />
            </div>
          )}
        </div>

        {/* Campaign Info */}
        <CollaborationInfoCard
          campaign={campaign}
          isDetailsOpen={isDetailsOpen}
          setIsDetailsOpen={setIsDetailsOpen}
        />

        {/* Activity Timeline */}
        {timeline && timeline.length > 0 && (
          <div>
            <p className="text-white/50 text-xs font-semibold uppercase tracking-widest mb-3">
              Activity
            </p>
            <TimelineCard timeline={timeline} />
          </div>
        )}
      </TabsContent>

      <TabsContent value="documents" className="mt-6">
        <CollaborationDocumentsCard collaborationId={campaign.id} />
      </TabsContent>

      <TabsContent value="events" className="mt-6">
        <CollaborationEventsTab campaignId={campaign.id} highlightEventId={highlightEventId} />
      </TabsContent>
    </Tabs>
  )
}

export default CollaborationDetailTabs
